/*
 * collection-template-new.js  ·  Oath Jewelry  ·  v1.25.11 (Nov 2026)
 *
 * Controller for the collection filter rail / mobile drawer and toolbar
 * (snippets/oath-collection-products.liquid). Loaded with `defer`.
 *
 * Filter values, chips and pagination are real links, so the page works without
 * this script. It adds:
 *   - in-place updates for filters, chips, sort, price and pagination (Section
 *     Rendering API), with back/forward support and a full-page fallback on any error
 *   - group collapse/expand and "Show all N" for long value lists
 *   - price presets, min/max apply + clear (a reversed min and max are swapped)
 *   - sort that waits for a choice when using the keyboard
 *   - mobile drawer: stays open while filtering, Escape, close buttons, backdrop, focus return
 *   - collection description "Read more" (all widths)
 * Event delegation keeps everything working after the results are swapped.
 */
(function () {
  'use strict';

  var region, sectionId, ctrl, live, lastSearch, loadedFaceted;
  var sortViaKey = false;
  var sortStart = null;
  var drawerMq = window.matchMedia('(max-width: 749px)');
  var FACETED = /[?&](filter\.|sort_by=)/;

  function q(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function clean(href) {
    var u = new URL(href, window.location.href);
    u.searchParams.delete('section_id');
    return u;
  }

  /* Links built while rendering a section response carry section_id; keep them shareable. */
  function tidy() {
    qa('a[href*="section_id="]', region).forEach(function (a) {
      var u = clean(a.getAttribute('href'));
      a.setAttribute('href', u.pathname + u.search);
    });
    qa('[data-oath-trigger]', region).forEach(syncTrigger);
  }

  function isOpen(filter) {
    var panel = q('[data-oath-panel]', filter);
    return drawerMq.matches ? !!panel && !panel.hidden : !filter.classList.contains('is-collapsed');
  }

  function syncTrigger(t) {
    var f = t.closest('[data-oath-filter]');
    if (f) t.setAttribute('aria-expanded', isOpen(f) ? 'true' : 'false');
  }

  function saveState() {
    var s = { drawer: !!q('[data-oath-filters].is-open', region), groups: {} };
    qa('[data-oath-filter]', region).forEach(function (f) {
      var p = q('[data-oath-panel]', f);
      if (!p) return;
      s.groups[p.id] = {
        collapsed: f.classList.contains('is-collapsed'),
        expanded: f.classList.contains('is-expanded'),
        hidden: p.hidden
      };
    });
    /* Remember the control in use, so keyboard users stay where they were. */
    var ae = document.activeElement;
    if (ae && region.contains(ae)) {
      var panel = ae.closest('[data-oath-panel]');
      var name = q('.oath-filter__option-name', ae);
      if (panel && name) s.focus = { panel: panel.id, name: name.textContent.trim() };
      else if (ae.matches('[data-oath-sort]')) s.focus = { sort: true };
    }
    return s;
  }

  function restoreState(s) {
    qa('[data-oath-filter]', region).forEach(function (f) {
      var p = q('[data-oath-panel]', f);
      var g = p && s.groups[p.id];
      if (!g) return;
      var active = f.classList.contains('is-active');
      f.classList.toggle('is-collapsed', g.collapsed && !active);
      if (g.expanded) {
        f.classList.add('is-expanded');
        var more = q('[data-oath-more]', f);
        if (more) {
          more.setAttribute('aria-expanded', 'true');
          more.textContent = more.getAttribute('data-label-less');
        }
      }
      p.hidden = g.hidden && !active;
      f.classList.toggle('is-open', !p.hidden);
    });
    qa('[data-oath-trigger]', region).forEach(syncTrigger);
    if (s.drawer) openDrawer(true);
    else document.documentElement.classList.remove('oath-drawer-lock');
  }

  function restoreFocus(s, grid) {
    var el = null;
    if (s.focus && s.focus.sort) el = q('[data-oath-sort]', region);
    if (s.focus && s.focus.panel) {
      var panel = document.getElementById(s.focus.panel);
      if (panel) {
        qa('.oath-filter__option', panel).some(function (o) {
          var n = q('.oath-filter__option-name', o);
          if (n && n.textContent.trim() === s.focus.name) { el = o; return true; }
          return false;
        });
      }
    }
    if (!el && s.drawer) el = q('.oath-filters__close', region);
    if (!el) el = grid;
    if (el) el.focus({ preventScroll: true });
  }

  function navigate(url, opts) {
    opts = opts || {};
    var target = clean(url);
    /* A page opened on a filtered view has no hero or description; clearing back to the main view needs a real load. */
    if (loadedFaceted && !FACETED.test(target.search)) {
      window.location.href = target.toString();
      return;
    }
    var fetchUrl = new URL(target.toString());
    fetchUrl.searchParams.set('section_id', sectionId);
    if (ctrl) ctrl.abort();
    ctrl = window.AbortController ? new AbortController() : null;
    region.classList.add('is-loading');
    region.setAttribute('aria-busy', 'true');
    var state = saveState();

    fetch(fetchUrl.toString(), { signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = q('[data-oath-results]', doc);
        if (!next) throw new Error('no results region');
        region.innerHTML = next.innerHTML;
        region.className = next.className;
        region.removeAttribute('aria-busy');
        tidy();
        restoreState(state);
        if (!opts.fromHistory) {
          window.history.pushState({ oathCollection: true }, '', target.pathname + target.search);
          lastSearch = window.location.search;
        }
        var count = q('.oath-toolbar__count', region);
        if (live && count) live.textContent = count.textContent.trim();
        var grid = q('#Collection', region);
        restoreFocus(state, grid);
        if (grid && !state.drawer) {
          if (opts.scroll || grid.getBoundingClientRect().top < 0) grid.scrollIntoView({ block: 'start' });
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        window.location.href = target.toString();
      });
  }

  function setParam(mutator) {
    var u = new URL(window.location.href);
    mutator(u);
    u.searchParams.delete('page');
    navigate(u.toString());
  }

  function setPrice(min, max) {
    var a = parseFloat(min);
    var b = parseFloat(max);
    if (!isNaN(a) && !isNaN(b) && a > b) {
      var t = min;
      min = max;
      max = t;
    }
    setParam(function (u) {
      if (min !== null && min !== '') u.searchParams.set('filter.v.price.gte', min);
      else u.searchParams.delete('filter.v.price.gte');
      if (max !== null && max !== '') u.searchParams.set('filter.v.price.lte', max);
      else u.searchParams.delete('filter.v.price.lte');
    });
  }

  function applySort(v) {
    sortViaKey = false;
    setParam(function (u) {
      if (v) u.searchParams.set('sort_by', v);
      else u.searchParams.delete('sort_by');
    });
  }

  function openDrawer(silent) {
    var root = q('[data-oath-filters]', region);
    if (!root) return;
    root.classList.add('is-open');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'oathFilterTitle');
    document.documentElement.classList.add('oath-drawer-lock');
    var opener = q('[data-oath-drawer-open]', region);
    if (opener) opener.setAttribute('aria-expanded', 'true');
    if (!silent) {
      /* Open the first group when none is, so the drawer never starts as a bare list of names. */
      var panels = qa('[data-oath-panel]', root);
      if (panels.length && !panels.some(function (p) { return !p.hidden; })) {
        panels[0].hidden = false;
        var f = panels[0].closest('[data-oath-filter]');
        f.classList.add('is-open');
        var tr = q('[data-oath-trigger]', f);
        if (tr) syncTrigger(tr);
      }
      var first = q('.oath-filters__close', root);
      if (first) first.focus();
    }
  }

  function closeDrawer(toResults) {
    var root = q('[data-oath-filters]', region);
    if (!root || !root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    root.removeAttribute('role');
    root.removeAttribute('aria-modal');
    root.removeAttribute('aria-labelledby');
    document.documentElement.classList.remove('oath-drawer-lock');
    var opener = q('[data-oath-drawer-open]', region);
    if (opener) { opener.setAttribute('aria-expanded', 'false'); opener.focus({ preventScroll: true }); }
    if (toResults) {
      var grid = q('#Collection', region);
      if (grid) grid.scrollIntoView({ block: 'start' });
    }
  }

  function onClick(e) {
    var t = e.target;
    var link = t.closest('a[href]');
    if (link && region.contains(link)) {
      if (link.closest('.oath-filter__option.is-disabled') || link.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
      var isFilter = link.matches('.oath-filter__option, .oath-chip, .oath-filters__clear, .oath-btn--ghost');
      var isPage = !!link.closest('.pagination-nav');
      if ((isFilter || isPage) && clean(link.href).pathname === window.location.pathname) {
        e.preventDefault();
        navigate(link.href, { scroll: isPage });
        return;
      }
    }

    var trigger = t.closest('[data-oath-trigger]');
    if (trigger) {
      var f = trigger.closest('[data-oath-filter]');
      var panel = q('[data-oath-panel]', f);
      if (drawerMq.matches) {
        panel.hidden = !panel.hidden;
        f.classList.toggle('is-open', !panel.hidden);
      } else {
        f.classList.toggle('is-collapsed');
      }
      syncTrigger(trigger);
      return;
    }

    var more = t.closest('[data-oath-more]');
    if (more) {
      var open = more.closest('[data-oath-filter]').classList.toggle('is-expanded');
      more.setAttribute('aria-expanded', open ? 'true' : 'false');
      more.textContent = open ? more.getAttribute('data-label-less') : more.getAttribute('data-label-more');
      return;
    }

    var preset = t.closest('[data-oath-price-preset]');
    if (preset) {
      if (preset.getAttribute('aria-pressed') === 'true') setPrice(null, null);
      else setPrice(preset.getAttribute('data-min'), preset.getAttribute('data-max'));
      return;
    }
    if (t.closest('[data-oath-price-apply]')) {
      var mn = q('[data-oath-price-min]', region), mx = q('[data-oath-price-max]', region);
      setPrice(mn ? mn.value : null, mx ? mx.value : null);
      return;
    }
    if (t.closest('[data-oath-price-clear]')) { setPrice(null, null); return; }
    if (t.closest('[data-oath-drawer-open]')) { openDrawer(); return; }
    var closer = t.closest('[data-oath-drawer-close]');
    if (closer) { closeDrawer(closer.classList.contains('oath-btn--solid')); return; }
    if (t.matches('[data-oath-filters].is-open')) closeDrawer();
  }

  function initRegion() {
    region = q('[data-oath-results]');
    if (!region) return;
    var wrap = region.closest('[data-section-id]');
    if (!wrap || !window.fetch || !window.DOMParser) return;
    sectionId = wrap.getAttribute('data-section-id');
    loadedFaceted = FACETED.test(window.location.search);
    lastSearch = window.location.search;

    live = document.createElement('div');
    live.className = 'visually-hidden';
    live.setAttribute('role', 'status');
    region.parentNode.insertBefore(live, region);

    tidy();
    region.addEventListener('click', onClick);

    /* Sort: a mouse or touch choice applies at once. Arrow keys only move through the list;
       Enter or leaving the field applies it, so one key press doesn't reload the results. */
    region.addEventListener('pointerdown', function (e) {
      if (e.target.matches('[data-oath-sort]')) sortViaKey = false;
    });
    region.addEventListener('focusin', function (e) {
      if (e.target.matches('[data-oath-sort]')) sortStart = e.target.value;
    });
    region.addEventListener('change', function (e) {
      if (e.target.matches('[data-oath-sort]') && !sortViaKey) applySort(e.target.value);
    });
    region.addEventListener('focusout', function (e) {
      if (e.target.matches('[data-oath-sort]') && sortViaKey) {
        sortViaKey = false;
        if (e.target.value !== sortStart) applySort(e.target.value);
      }
    });

    region.addEventListener('keydown', function (e) {
      if (e.target.matches('[data-oath-sort]')) {
        if (e.key === 'Enter') {
          if (e.target.value !== sortStart) { e.preventDefault(); applySort(e.target.value); }
        } else if (e.key !== 'Tab' && e.key !== 'Escape' && e.key !== 'Shift') {
          sortViaKey = true;
        }
        return;
      }
      if (e.key === 'Enter' && e.target.matches('[data-oath-price-min], [data-oath-price-max]')) {
        e.preventDefault();
        var mn = q('[data-oath-price-min]', region), mx = q('[data-oath-price-max]', region);
        setPrice(mn.value, mx.value);
      }
    });

    document.addEventListener('keydown', function (e) {
      var root = q('[data-oath-filters].is-open', region);
      if (!root) return;
      if (e.key === 'Escape') { closeDrawer(); return; }
      if (e.key === 'Tab') {
        var d = q('[data-oath-drawer]', root);
        var vis = qa('a[href]:not([tabindex="-1"]), button:not([disabled]), input, select', d).filter(function (el) { return el.offsetParent !== null; });
        if (!vis.length) return;
        var a = vis[0], z = vis[vis.length - 1];
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
        else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
      }
    });

    /* Only a changed query string needs new results; the skip link's #Collection hash does not. */
    window.addEventListener('popstate', function () {
      if (window.location.search === lastSearch) return;
      lastSearch = window.location.search;
      navigate(window.location.href, { fromHistory: true });
    });

    if (drawerMq.addEventListener) {
      drawerMq.addEventListener('change', function () {
        if (!drawerMq.matches) closeDrawer();
        qa('[data-oath-trigger]', region).forEach(syncTrigger);
      });
    }
  }

  /* ---- Description "Read more" (all widths; first paragraph clamped to 2 lines) ---- */
  function initDescription() {
    var wrap = q('[data-oath-collection-desc]');
    if (!wrap) return;
    var text = q('[data-oath-desc-text]', wrap);
    var btn = q('[data-oath-desc-toggle]', wrap);
    if (!text || !btn) return;
    var queued = false;
    var evaluate = function () {
      queued = false;
      if (wrap.classList.contains('is-expanded')) return;
      var p = q('p', text);
      var blocks = text.children.length - (q('h2', text) ? 1 : 0);
      var clipped = p && p.scrollHeight > p.clientHeight + 2;
      btn.hidden = !(clipped || blocks > 1);
    };
    var schedule = function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(evaluate);
    };
    btn.addEventListener('click', function () {
      var expanded = wrap.classList.toggle('is-expanded');
      btn.textContent = expanded ? 'Read less' : 'Read more';
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
    schedule();
    window.addEventListener('resize', schedule, { passive: true });
  }

  function init() {
    initRegion();
    initDescription();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
